"use client";

import {
  useCursorPagination,
  type UseCursorPaginationResult,
} from "@/hooks/use-cursor-pagination";
import { useCallback, useEffect, useState } from "react";

// Search + keyset pagination for a list page, in one piece. Wiring these by
// hand is subtly dangerous: the debounced query feeds `resetKey`, so every
// keystroke flips `loading` back to true. Pages that then rendered their
// search box inside the `{loading ? … }` branch unmounted the input
// mid-keystroke and stole focus from the user. This hook derives the display
// flags that keep that from happening, and `SearchableList` consumes them.

const DEFAULT_DEBOUNCE_MS = 300;

export type UseSearchableListOptions<TItem, TCursor> = {
  pageSize: number;
  /** Pagination stays idle — and the skeleton stays up — until this is true. */
  ready?: boolean;
  /** Receives the debounced, trimmed query; never the raw input value. */
  fetchPage: (
    cursor: TCursor | null,
    pageSize: number,
    search: string
  ) => Promise<TItem[]>;
  getCursor: (item: TItem) => TCursor;
  getKey: (item: TItem) => string;
  /** Any further value that should force a reload from the first page. */
  resetKey?: string | number | boolean | null;
  /**
   * True while a filter outside the search box is narrowing the list. Keeps
   * `showEmptyState` down so a filter that excludes everything reports "nothing
   * matched" rather than replacing the body — and the filter control with it —
   * by the page's "you have none of these at all" empty state.
   */
  filtered?: boolean;
  debounceMs?: number;
};

export type UseSearchableListResult<TItem> = UseCursorPaginationResult<TItem> & {
  search: string;
  setSearch: (value: string) => void;
  /** Trimmed and debounced — what the current results were actually fetched with. */
  debouncedSearch: string;
  isSearching: boolean;
  isEmptyResult: boolean;
  /** Nothing to show yet, as opposed to a refetch over existing results. */
  showSkeleton: boolean;
  /** Genuinely empty, not merely filtered to nothing. */
  showEmptyState: boolean;
};

export function useSearchableList<TItem, TCursor>({
  pageSize,
  ready = true,
  fetchPage,
  getCursor,
  getKey,
  resetKey,
  filtered = false,
  debounceMs = DEFAULT_DEBOUNCE_MS,
}: UseSearchableListOptions<TItem, TCursor>): UseSearchableListResult<TItem> {
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  useEffect(() => {
    const handle = setTimeout(() => setDebouncedSearch(search.trim()), debounceMs);
    return () => clearTimeout(handle);
  }, [search, debounceMs]);

  const gatedFetchPage = useCallback(
    (cursor: TCursor | null, size: number) =>
      ready ? fetchPage(cursor, size, debouncedSearch) : Promise.resolve<TItem[]>([]),
    [ready, fetchPage, debouncedSearch]
  );

  const pagination = useCursorPagination<TItem, TCursor>({
    pageSize,
    fetchPage: gatedFetchPage,
    getCursor,
    getKey,
    resetKey: `${ready}:${debouncedSearch}:${resetKey ?? ""}`,
  });

  // `search` and `debouncedSearch` are both consulted so the empty state can't
  // flash in during the debounce window and yank the search box off screen.
  const isSearching = search.length > 0 || debouncedSearch.length > 0;
  const isEmptyResult = pagination.items.length === 0;

  return {
    ...pagination,
    search,
    setSearch,
    debouncedSearch,
    isSearching,
    isEmptyResult,
    showSkeleton: !ready || (pagination.loading && isEmptyResult),
    showEmptyState:
      ready && !pagination.loading && !isSearching && !filtered && isEmptyResult,
  };
}
